import { invertMatrix, transformPoint, type Point } from '../core/geometry';
import type { PathGeometry, PathNode, PathObject, VectorObject, WidthStop } from './types';

export const DYNAMIC_CORNERS_EXTENSION = 'maipo:dynamic-corners';
export type DynamicCornerMode = 'relative' | 'absolute';

export function dynamicCornerMode(object: PathObject): DynamicCornerMode {
  return object.extensions[DYNAMIC_CORNERS_EXTENSION] === 'absolute' ? 'absolute' : 'relative';
}

export function withDynamicCornerMode(object: PathObject, mode: DynamicCornerMode): PathObject {
  const extensions = { ...object.extensions };
  if (mode === 'absolute') extensions[DYNAMIC_CORNERS_EXTENSION] = mode;
  else delete extensions[DYNAMIC_CORNERS_EXTENSION];
  return { ...object, extensions };
}

const distance = (left: Point, right: Point) => Math.hypot(right.x - left.x, right.y - left.y);

function roundedCornerNodes(previous: PathNode, node: PathNode, next: PathNode): PathNode[] | null {
  const radius = Math.max(0, node.cornerRadius ?? 0);
  if (
    radius <= 0
    || previous.outgoing
    || node.incoming
    || node.outgoing
    || next.incoming
  ) return null;
  const incomingLength = distance(previous.anchor, node.anchor);
  const outgoingLength = distance(node.anchor, next.anchor);
  if (incomingLength < 0.001 || outgoingLength < 0.001) return null;
  const toPrevious = {
    x: (previous.anchor.x - node.anchor.x) / incomingLength,
    y: (previous.anchor.y - node.anchor.y) / incomingLength,
  };
  const toNext = {
    x: (next.anchor.x - node.anchor.x) / outgoingLength,
    y: (next.anchor.y - node.anchor.y) / outgoingLength,
  };
  const interior = Math.acos(Math.max(-1, Math.min(1, toPrevious.x * toNext.x + toPrevious.y * toNext.y)));
  if (interior < 0.02 || Math.abs(Math.PI - interior) < 0.02) return null;
  const trim = Math.min(radius / Math.tan(interior / 2), incomingLength * 0.49, outgoingLength * 0.49);
  const effectiveRadius = trim * Math.tan(interior / 2);
  const sweep = Math.PI - interior;
  const control = 4 / 3 * Math.tan(sweep / 4) * effectiveRadius;
  const entry = {
    x: node.anchor.x + toPrevious.x * trim,
    y: node.anchor.y + toPrevious.y * trim,
  };
  const exit = {
    x: node.anchor.x + toNext.x * trim,
    y: node.anchor.y + toNext.y * trim,
  };
  const base = { ...node };
  delete base.cornerRadius;
  return [
    {
      ...base,
      id: `${node.id}:corner:entry`,
      anchor: entry,
      incoming: null,
      outgoing: {
        x: entry.x - toPrevious.x * control,
        y: entry.y - toPrevious.y * control,
      },
    },
    {
      ...base,
      id: `${node.id}:corner:exit`,
      anchor: exit,
      incoming: {
        x: exit.x - toNext.x * control,
        y: exit.y - toNext.y * control,
      },
      outgoing: null,
    },
  ];
}

/** Returns render-time fillets while leaving the authored anchors intact. */
export function effectivePathGeometry(geometry: PathGeometry): PathGeometry {
  if (!geometry.nodes.some((node) => (node.cornerRadius ?? 0) > 0)) return geometry;
  const source = new Map(geometry.nodes.map((node) => [node.id, node]));
  const nodes: PathNode[] = [];
  const subpaths = geometry.subpaths.map((subpath) => {
    const ordered = subpath.nodeIds.flatMap((id) => {
      const node = source.get(id);
      return node ? [node] : [];
    });
    const nodeIds: string[] = [];
    ordered.forEach((node, index) => {
      const previous = subpath.closed ? ordered[index - 1] ?? ordered.at(-1) : ordered[index - 1];
      const next = subpath.closed ? ordered[index + 1] ?? ordered[0] : ordered[index + 1];
      const rounded = previous && next ? roundedCornerNodes(previous, node, next) : null;
      const additions = rounded ?? [{ ...node }];
      nodes.push(...additions);
      nodeIds.push(...additions.map((addition) => addition.id));
    });
    return { ...subpath, nodeIds };
  });
  return { ...geometry, nodes, subpaths };
}

/** Generates absolute-radius corners in transformed space, then maps them back for normal rendering. */
export function effectiveObjectPathGeometry(object: PathObject): PathGeometry {
  if (dynamicCornerMode(object) !== 'absolute' || !object.geometry.nodes.some((node) => (node.cornerRadius ?? 0) > 0)) return effectivePathGeometry(object.geometry);
  const linear = [object.transform[0], object.transform[1], object.transform[2], object.transform[3], 0, 0] as const;
  const inverse = invertMatrix(linear);
  if (!inverse) return effectivePathGeometry(object.geometry);
  const transformed: PathGeometry = {
    ...object.geometry,
    nodes: object.geometry.nodes.map((node) => ({
      ...node,
      anchor: transformPoint(linear, node.anchor),
      incoming: node.incoming ? transformPoint(linear, node.incoming) : null,
      outgoing: node.outgoing ? transformPoint(linear, node.outgoing) : null,
    })),
  };
  const effective = effectivePathGeometry(transformed);
  return {
    ...effective,
    nodes: effective.nodes.map((node) => ({
      ...node,
      anchor: transformPoint(inverse, node.anchor),
      incoming: node.incoming ? transformPoint(inverse, node.incoming) : null,
      outgoing: node.outgoing ? transformPoint(inverse, node.outgoing) : null,
    })),
  };
}

function midpoint(left: Point, right: Point): Point {
  return { x: (left.x + right.x) / 2, y: (left.y + right.y) / 2 };
}

function lineDistance(point: Point, start: Point, end: Point): number {
  const length = distance(start, end);
  if (length < 0.000001) return distance(point, start);
  return Math.abs(((end.y - start.y) * point.x) - ((end.x - start.x) * point.y) + (end.x * start.y) - (end.y * start.x)) / length;
}

function flattenCubic(start: Point, control1: Point, control2: Point, end: Point, output: Point[], tolerance: number, depth = 0): void {
  if (depth >= 12 || Math.max(lineDistance(control1, start, end), lineDistance(control2, start, end)) <= tolerance) {
    output.push(end);
    return;
  }
  const a = midpoint(start, control1);
  const b = midpoint(control1, control2);
  const c = midpoint(control2, end);
  const d = midpoint(a, b);
  const e = midpoint(b, c);
  const center = midpoint(d, e);
  flattenCubic(start, a, d, center, output, tolerance, depth + 1);
  flattenCubic(center, e, c, end, output, tolerance, depth + 1);
}

export function flattenPathGeometry(source: PathGeometry, tolerance = 0.5): Array<{ points: Point[]; closed: boolean }> {
  const geometry = effectivePathGeometry(source);
  const nodes = new Map(geometry.nodes.map((node) => [node.id, node]));
  return geometry.subpaths.flatMap((subpath) => {
    const ordered = subpath.nodeIds.flatMap((id) => {
      const node = nodes.get(id);
      return node ? [node] : [];
    });
    const first = ordered[0];
    if (!first) return [];
    const points: Point[] = [{ ...first.anchor }];
    for (let index = 1; index < ordered.length; index += 1) {
      const previous = ordered[index - 1];
      const node = ordered[index];
      if (!previous || !node) continue;
      if (previous.outgoing || node.incoming) flattenCubic(previous.anchor, previous.outgoing ?? previous.anchor, node.incoming ?? node.anchor, node.anchor, points, tolerance);
      else points.push({ ...node.anchor });
    }
    const last = ordered.at(-1);
    if (subpath.closed && last) {
      if (last.outgoing || first.incoming) flattenCubic(last.anchor, last.outgoing ?? last.anchor, first.incoming ?? first.anchor, first.anchor, points, tolerance);
      else points.push({ ...first.anchor });
    }
    return [{ points, closed: subpath.closed }];
  });
}

export function widthAt(stops: readonly WidthStop[] | undefined, fallback: number, t: number): number {
  if (!stops?.length) return Math.max(0, fallback);
  const ordered = [...stops].sort((left, right) => left.t - right.t);
  const position = Math.max(0, Math.min(1, t));
  const first = ordered[0]!;
  const last = ordered.at(-1)!;
  if (position <= first.t) return Math.max(0, first.width);
  if (position >= last.t) return Math.max(0, last.width);
  const rightIndex = ordered.findIndex((stop) => stop.t >= position);
  const right = ordered[rightIndex] ?? last;
  const left = ordered[Math.max(0, rightIndex - 1)] ?? first;
  const amount = (position - left.t) / Math.max(0.000001, right.t - left.t);
  return Math.max(0, left.width + (right.width - left.width) * amount);
}

/** Generates fill polygons used for variable-width strokes in display and export. */
export function variableWidthOutlines(geometry: PathGeometry, fallback: number): Point[][] {
  return flattenPathGeometry(geometry, 0.35).flatMap(({ points, closed }) => {
    if (points.length < 2) return [];
    const lengths = [0];
    for (let index = 1; index < points.length; index += 1) lengths.push((lengths.at(-1) ?? 0) + distance(points[index - 1]!, points[index]!));
    const total = lengths.at(-1) ?? 0;
    if (total < 0.001) return [];
    const left: Point[] = [];
    const right: Point[] = [];
    points.forEach((point, index) => {
      const previous = points[index - 1] ?? (closed ? points.at(-2) : point);
      const next = points[index + 1] ?? (closed ? points[1] : point);
      if (!previous || !next) return;
      const dx = next.x - previous.x;
      const dy = next.y - previous.y;
      const length = Math.hypot(dx, dy) || 1;
      const half = widthAt(geometry.widthStops, fallback, (lengths[index] ?? 0) / total) / 2;
      const normal = { x: -dy / length, y: dx / length };
      left.push({ x: point.x + normal.x * half, y: point.y + normal.y * half });
      right.push({ x: point.x - normal.x * half, y: point.y - normal.y * half });
    });
    return [[...left, ...right.reverse()]];
  });
}

// Keyed by geometry, not the object: node-level commands replace `geometry`
// while keeping the object identity, so the object itself is not a safe cache key.
const paths = new WeakMap<VectorObject['geometry'], Path2D>();

export function vectorObjectPath(object: VectorObject): Path2D {
  const cached = paths.get(object.geometry);
  if (cached) return cached;
  const path = new Path2D();
  if (object.type === 'rectangle') {
    const { x, y, width, height, radius } = object.geometry;
    const maximum = Math.min(Math.abs(width) / 2, Math.abs(height) / 2);
    path.roundRect(x, y, width, height, [radius.topLeft, radius.topRight, radius.bottomRight, radius.bottomLeft].map((value) => Math.max(0, Math.min(value, maximum))));
  } else if (object.type === 'ellipse') {
    const { x, y, width, height } = object.geometry;
    path.ellipse(x + width / 2, y + height / 2, Math.abs(width / 2), Math.abs(height / 2), 0, 0, Math.PI * 2);
  } else {
    const geometry = effectiveObjectPathGeometry(object);
    const nodes = new Map(geometry.nodes.map((node) => [node.id, node]));
    for (const subpath of geometry.subpaths) {
      const ordered = subpath.nodeIds.flatMap((id) => {
        const node = nodes.get(id);
        return node ? [node] : [];
      });
      const first = ordered[0];
      if (!first) continue;
      path.moveTo(first.anchor.x, first.anchor.y);
      for (let index = 1; index < ordered.length; index += 1) {
        const previous = ordered[index - 1];
        const node = ordered[index];
        if (!previous || !node) continue;
        if (previous.outgoing || node.incoming) {
          const outgoing = previous.outgoing ?? previous.anchor;
          const incoming = node.incoming ?? node.anchor;
          path.bezierCurveTo(outgoing.x, outgoing.y, incoming.x, incoming.y, node.anchor.x, node.anchor.y);
        } else path.lineTo(node.anchor.x, node.anchor.y);
      }
      if (subpath.closed) {
        const last = ordered.at(-1);
        if (last && (last.outgoing || first.incoming)) {
          const outgoing = last.outgoing ?? last.anchor;
          const incoming = first.incoming ?? first.anchor;
          path.bezierCurveTo(outgoing.x, outgoing.y, incoming.x, incoming.y, first.anchor.x, first.anchor.y);
        }
        path.closePath();
      }
    }
  }
  paths.set(object.geometry, path);
  return path;
}
